import { useEffect } from "react";
import { create } from "zustand";

/**
 * Seller theme (light / dark) with `system` following the OS preference.
 *
 * The persisted preference is applied to `<html>` as a `.dark` class. A tiny
 * inline script in index.html applies it before first paint (no flash); this
 * store keeps it in sync at runtime and drives the toggle in the account menu.
 */
export type ThemePref = "light" | "dark" | "system";

const STORAGE_KEY = "seller_theme";

function readStoredPref(): ThemePref {
  if (typeof window === "undefined") return "system";
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "light" || value === "dark" || value === "system") {
      return value;
    }
  } catch {
    // ignore (private mode / disabled storage)
  }
  return "system";
}

function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function resolveDark(pref: ThemePref): boolean {
  return pref === "dark" || (pref === "system" && prefersDark());
}

function applyToDom(pref: ThemePref): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveDark(pref));
}

type ThemeState = {
  pref: ThemePref;
  /** True when the effective theme is dark (accounts for `system`). */
  resolvedDark: boolean;
  setPref: (pref: ThemePref) => void;
  /** Convenience explicit light↔dark flip (sets an explicit pref). */
  toggle: () => void;
};

export const useThemeStore = create<ThemeState>((set, get) => ({
  pref: readStoredPref(),
  resolvedDark: resolveDark(readStoredPref()),
  setPref: (pref) => {
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      // ignore
    }
    applyToDom(pref);
    set({ pref, resolvedDark: resolveDark(pref) });
  },
  toggle: () => {
    const next: ThemePref = get().resolvedDark ? "light" : "dark";
    get().setPref(next);
  },
}));

/**
 * Applies the current preference to the DOM and keeps it in sync with OS
 * changes while the preference is `system`. Mount once near the app root.
 */
export function useApplyTheme(): void {
  const pref = useThemeStore((s) => s.pref);

  useEffect(() => {
    applyToDom(pref);
    if (pref !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      applyToDom("system");
      useThemeStore.setState({ resolvedDark: resolveDark("system") });
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [pref]);
}
